<template>
	<!-- eslint-disable max-lines -->
	<div class="data-selection-container">
		<QuestionGroup>
			<!-- Step 1: Data Type Selection -->
			<QuestionCard
				title="What type of data do you want to send?"
				description="Choose the category of information from your system that this integration will work with."
			>
				<Dropdown
					label="Data Type *"
					:value="cassieObjectId"
					:items="dataTypeItems"
					:disabled="!userFullPermissions || isEdit"
					rules="required"
					class="data-type-dropdown"
					@input="$emit('update:cassieObjectId', $event)"
				/>
			</QuestionCard>

			<!-- Step 2: Field Selection -->
			<QuestionCard
				title="Which fields do you want to include?"
				description="Select the specific fields that you want to send in your integration."
			>
				<div class="dropdown-wrapper">
					<v-select
						:value="selectedFieldValues"
						:items="enrichedFieldItems"
						:disabled="!userFullPermissions"
						label="Fields *"
						multiple
						outlined
						dense
						:rules="[v => (selectedFieldValues && selectedFieldValues.length > 0) || 'Please select at least one field']"
						hide-details
						class="field-selection-dropdown data-type-dropdown"
						@input="handleFieldSelection"
					>
						<template #selection="{ index }">
							<!-- Empty selection template to hide chips inside select -->
							<span
								v-if="index === 0"
								class="grey--text"
							>
								{{ selectedFields.length }} field{{ selectedFields.length !== 1 ? 's' : '' }} selected
							</span>
						</template>
						<template #item="{ item }">
							<div
								class="field-item-content clickable-row"
								@click.stop="toggleFieldSelection(item)"
							>
								<v-checkbox
									:input-value="isFieldSelected(item.value)"
									class="field-item-checkbox mr-2"
									hide-details
									@click.stop="toggleFieldSelection(item)"
								/>
								<v-icon
									small
									class="field-item-icon mr-2"
								>
									{{ getFieldIcon(item.typeFriendlyName) }}
								</v-icon>
								<div class="field-item-text">
									<span class="field-item-name">
										{{ item.text }}
									</span>
									<span class="field-type">
										{{ item.typeFriendlyName }}
									</span>
									<v-chip
										v-if="item.isFilterable"
										x-small
										class="ml-2 mono-chip"
									>
										Filterable
									</v-chip>
									<v-chip
										v-if="item.isIncremental"
										x-small
										class="ml-2 mono-chip"
									>
										Incremental
									</v-chip>
								</div>
							</div>
						</template>
					</v-select>
				</div>

				<!-- Selected field chips displayed below the select -->
				<div
					v-if="selectedFields.length > 0"
					class="selected-fields-wrapper"
				>
					<div class="selected-fields-chips">
						<v-chip
							v-for="field in selectedFields"
							:key="getFieldKey(field)"
							small
							close
							class="mr-2 mb-2 square-chip"
							@click:close="removeField({ value: getFieldValue(field), text: getFieldDisplayName(field) })"
						>
							<v-icon
								small
								class="field-chip-icon mr-1"
							>
								{{ getFieldIconForSelectedField(field) }}
							</v-icon>
							{{ getFieldDisplayName(field) }}
						</v-chip>
					</div>
				</div>
			</QuestionCard>

			<!-- Step 3: Field Filtering -->
			<QuestionCard
				title="Do you want to apply filters to your data?"
				description="Add conditions to only send data that meets certain criteria. Filters help reduce the amount of data transferred and ensure only relevant records are sent."
				show-yes-no-buttons
				:value="useFiltering"
				:disabled="!userFullPermissions"
				@input="setUseFiltering"
			>
				<FilteringInterface
					v-if="useFiltering"
					:cassie-object-id="cassieObjectId"
					:data-type-items="dataTypeItems"
					:available-fields="selectedFieldsOnly"
					:user-full-permissions="userFullPermissions"
					:initial-filters="integrationFilters"
					:lookup-data="lookupData"
					@update:filters="$emit('update:integrationFilters', $event)"
				/>
			</QuestionCard>

			<!-- Step 4: Tracking Field Selection -->
			<QuestionCard
				title="Which field should we use to track changes?"
				description="Select the field used to detect new records to be synced. Only records with values greater than the last synced value will be included."
			>
				<Dropdown
					label="Tracking Field *"
					:value="objectIdColumn"
					:items="incrementFieldItems"
					rules="required"
					:disabled="!userFullPermissions || isEdit"
					class="tracking-field-dropdown"
					@input="$emit('update:objectIdColumn', $event)"
				/>
			</QuestionCard>

			<!-- Step 5: Historical Data Choice (hidden when service is enabled) -->
			<IncrementalModeSelector
				v-if="!integrationServiceEnabled"
				:key="`incremental-${normalizedResetIncrementalValue}`"
				:selected-data-type-name="selectedDataTypeName"
				:reset-incremental-value="normalizedResetIncrementalValue"
				:object-id-column="objectIdColumn"
				:increment-field-items="incrementFieldItems"
				:user-full-permissions="userFullPermissions"
				:is-new-integration="isNewIntegration"
				:integration-service-enabled="integrationServiceEnabled"
				@update:resetIncrementalValue="handleResetIncrementalValue"
				@update:objectIdColumn="$emit('update:objectIdColumn', $event)"
			/>
		</QuestionGroup>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { VIcon } from 'vuetify/lib/components'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import FilteringInterface from './FilteringInterface.vue'
import IncrementalModeSelector from './IncrementalModeSelector.vue'
import { QuestionCard, QuestionGroup } from '../../../../../components/component-library-wrapper.js'
import { getFieldIcon } from '../integration-builder/filterUtils.js'

export default {
	name: 'data-selection',
	components: {
		Dropdown,
		FilteringInterface,
		IncrementalModeSelector,
		QuestionCard,
		QuestionGroup,
		VIcon
	},
	props: {
		cassieObjectId: {
			type: Number,
			default: null
		},
		dataTypeItems: {
			type: Array,
			default: () => []
		},
		selectedFields: {
			type: Array,
			default: () => []
		},
		integrationFilters: {
			type: Array,
			default: () => []
		},
		objectIdColumn: {
			type: String,
			default: null
		},
		resetIncrementalValue: {
			type: Boolean,
			default: false
		},
		incrementFieldItems: {
			type: Array,
			default: () => []
		},
		availableFields: {
			type: Array,
			default: () => []
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		},
		lookupData: {
			type: Object,
			default: () => ({})
		},
		// Controls visibility of IncrementalModeSelector; hide when enabled
		integrationServiceEnabled: {
			type: Boolean,
			default: false
		},
		isNewIntegration: {
			type: Boolean,
			default: false
		},
		isEdit: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			resetPreferenceSelected: false,
			useFiltering: false
		}
	},
	computed: {
		normalizedResetIncrementalValue () {
			// Return the actual value without normalization to allow null state
			return this.resetIncrementalValue
		},
		selectedDataTypeName () {
			const dataType = this.dataTypeItems.find(item => item.value === this.cassieObjectId)
			return dataType?.text || ''
		},
		hasSelectedFields () {
			return this.selectedFields && this.selectedFields.length > 0
		},
		selectedFieldsOnly () {
			// Return only the selected fields for filtering
			if (!this.selectedFields || !Array.isArray(this.selectedFields)) {
				return []
			}

			return this.availableFields.filter(field => {
				return this.selectedFields.some(selected => {
					// Handle different data formats for backward compatibility
					if (typeof selected === 'string') {
						return selected === field.fieldName
					}
					return selected.fieldName === field.fieldName ||
						selected.cassieObjectFieldName === field.fieldName
				})
			})
		},
		availableFieldItems () {
			// Convert available fields to dropdown items
			return this.availableFields.map(field => ({
				text: field.displayName || field.fieldName,
				value: field.fieldName,
				fieldName: field.fieldName,
				typeFriendlyName: field.typeFriendlyName
			}))
		},
		enrichedFieldItems () {
			// Convert available fields to enriched dropdown items with metadata
			return this.availableFields.map(field => ({
				text: field.displayName || field.fieldName,
				value: field.fieldName,
				fieldName: field.fieldName,
				typeFriendlyName: field.typeFriendlyName,
				dataType: field.dataType,
				isFilterable: field.isFilterable,
				isIncremental: field.isIncremental
			}))
		},
		selectedFieldValues () {
			// Extract just the field names for v-select binding
			if (!this.selectedFields || !Array.isArray(this.selectedFields)) {
				return []
			}

			return this.selectedFields.map(field => {
				// Handle different data formats for backward compatibility
				if (typeof field === 'string') {
					return field
				}
				return field.fieldName || field.cassieObjectFieldName || field
			}).filter(Boolean)
		}
	},
	watch: {
		cassieObjectId (newValue, oldValue) {
			if (newValue !== oldValue) {
				// Reset selected fields and filters when data type changes
				this.$emit('update:objectSelectList', [])
				this.$emit('update:integrationFilters', [])
				// Also reset the tracking field when data type changes
				this.$emit('update:objectIdColumn', null)
				this.useFiltering = false
			}
		},
		resetIncrementalValue: {
			immediate: true,
			handler (newValue, oldValue) {
				// Handle reset incremental value changes
				if (newValue !== oldValue) {
					// Value changed, component will re-render automatically
				}
			}
		},
		objectIdColumn (newValue, oldValue) {
			// Reset the preference selection when incremental field changes
			// This handles the case where parent component automatically changes the field
			if (newValue !== oldValue && oldValue !== undefined) {
				this.resetPreferenceSelected = false
				this.useFiltering = false
			}
		},
		integrationFilters: {
			handler (newFilters) {
				// If filters exist (e.g., editing an existing integration), ensure filtering is enabled.
				// Do NOT automatically disable when filters are cleared; the Yes/No toggle controls that.
				if (Array.isArray(newFilters) && newFilters.length > 0) {
					this.useFiltering = true
				}
			},
			immediate: true
		}
	},
	methods: {
		getFieldIcon (typeFriendlyName) {
			return getFieldIcon(typeFriendlyName)
		},
		getFieldIconForSelectedField (selectedField) {
			// Find the full field object from availableFields to get the correct type
			let fieldName = selectedField.fieldName || selectedField.cassieObjectFieldName || selectedField
			if (typeof selectedField === 'string') {
				fieldName = selectedField
			}

			const fullField = this.availableFields.find(field =>
				field.fieldName === fieldName || field.cassieObjectFieldName === fieldName
			)

			return getFieldIcon(fullField?.typeFriendlyName || 'text')
		},
		handleResetIncrementalValue (value) {
			this.resetPreferenceSelected = true
			this.$emit('update:resetIncrementalValue', value)
			// Force child component to re-render with new value
			this.$forceUpdate()
		},
		removeField (item) {
			if (!this.userFullPermissions) return

			// Remove the field from the selected fields list
			const updatedFields = this.selectedFields.filter(field => {
				// Handle different data formats for backward compatibility
				if (typeof field === 'string') {
					return field !== item.value
				}
				return field.fieldName !== item.value && field.cassieObjectFieldName !== item.value
			})

			// Remove any filters that were applied to this field
			const updatedFilters = this.integrationFilters.filter(filter => {
				return filter.fieldName !== item.value
			})

			// Emit both updates
			this.$emit('update:objectSelectList', updatedFields)
			this.$emit('update:integrationFilters', updatedFilters)
		},
		handleFieldSelection (selectedFieldNames) {
			if (!this.userFullPermissions) return

			// Convert selected field names back to field objects
			const selectedFieldObjects = selectedFieldNames.map(fieldName => {
				const field = this.availableFields.find(f => f.fieldName === fieldName)
				if (field) {
					return {
						fieldName: field.fieldName,
						cassieObjectFieldName: field.fieldName,
						cassieObjectFieldDisplayName: field.displayName || field.fieldName,
						typeFriendlyName: field.typeFriendlyName,
						dataType: field.dataType,
						isFilterable: field.isFilterable,
						isIncremental: field.isIncremental
					}
				}
				return null
			}).filter(Boolean)

			this.$emit('update:objectSelectList', selectedFieldObjects)
		},
		setUseFiltering (value) {
			this.useFiltering = value
			if (!value) {
				// Clear filters when "No" is selected
				this.$emit('update:integrationFilters', [])
			}
		},
		isFieldSelected (fieldValue) {
			return this.selectedFieldValues.includes(fieldValue)
		},
		toggleFieldSelection (item) {
			const currentValues = [...this.selectedFieldValues]
			const index = currentValues.indexOf(item.value)

			if (index > -1) {
				// Remove the field
				currentValues.splice(index, 1)
			} else {
				// Add the field
				currentValues.push(item.value)
			}

			// Trigger the existing handleFieldSelection method
			this.handleFieldSelection(currentValues)
		},
		// Helper methods for field display
		getFieldKey (field) {
			if (typeof field === 'string') {
				return field
			}
			return field.fieldName || field.cassieObjectFieldName || JSON.stringify(field)
		},
		getFieldValue (field) {
			if (typeof field === 'string') {
				return field
			}
			return field.fieldName || field.cassieObjectFieldName || field
		},
		getFieldDisplayName (field) {
			if (typeof field === 'string') {
				return field
			}
			if (field.cassieObjectFieldDisplayName) {
				return field.cassieObjectFieldDisplayName
			}
			if (field.displayName) {
				return field.displayName
			}
			if (field.fieldName) {
				return field.fieldName
			}
			// Fallback for any other format
			return field.toString ? field.toString() : 'Unknown Field'
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../shared/stepper-styles.scss';

.data-selection-container {
	display: flex;
	flex-direction: column;
	gap: 0;
	max-width: 100%;
	box-sizing: border-box;
}

/* Removed unused CSS - questions now use QuestionCard component */

.filtering-buttons {
  display: flex;
  gap: 8px;
  align-self: flex-start;
  margin-bottom: 16px;
}

.filtering-btn {
  min-width: 60px;
  height: 32px;
}

/* Form Controls - restore proper @extend usage */
.data-type-dropdown,
.tracking-field-dropdown {
	@extend .form-dropdown;
}

.field-selection-dropdown {
	@extend .form-dropdown;
	background-color: #fff !important;

	.v-input__control {
		min-height: 40px !important;
	}

	.v-input__slot {
		min-height: 40px !important;
		padding-top: 0 !important;
		padding-bottom: 0 !important;
		background-color: white !important;
	}

	.v-select__selections {
		min-height: 38px;
		padding: 6px 12px;
		background-color: white !important;
	}
}

.selected-fields-wrapper {
	margin-top: 16px;
	padding: 12px 0;
	background: transparent;
}

.selected-fields-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.square-chip {
	border-color: var(--global-border-color) !important;
	background-color: #ffffff !important;
	border-radius: var(--radius-lg, 12px) !important;
	height: 32px !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
	transition: all 0.2s ease;

	&:hover {
		border-color: var(--global-border-color) !important;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		transform: translateY(-1px);
		background-color: var(--global-main-background-color) !important;
	}

	:deep(.v-chip__content) {
		padding: 0 12px !important;
	}

	:deep(.v-chip) {
		background-color: inherit !important;
		border: inherit !important;
	}
}

.field-chip-icon {
	color: var(--global-muted-foreground);
}

/* Full width dropdown styling */
.field-selection-dropdown.full-width {
	width: 100% !important;
	max-width: none !important;
}

/* Enriched field item styling */
.field-item-content {
	display: flex;
	align-items: center;
	width: 100%;
	padding: 8px 0;
}

.field-item-content.clickable-row {
	cursor: pointer;
	padding: 8px 12px;
	margin: 0 -12px;
	border-radius: var(--radius-sm, 4px);
	transition: background-color 0.2s ease;
}

.field-item-content.clickable-row:hover {
	background-color: var(--global-main-background-color);
}

.field-item-checkbox {
	flex-shrink: 0;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}

.field-item-icon {
	color: var(--global-muted-foreground);
	flex-shrink: 0;
}

.field-item-text {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.field-item-name {
	font-weight: 500;
	color: var(--global-foreground);
	font-size: 14px;
}

.field-type {
	font-size: 12px;
	color: var(--global-foreground);
	font-style: italic;
}

.mono-chip {
	background-color: #ffffff !important;
	color: var(--global-muted-foreground) !important;
	border: 1px solid var(--global-border-color) !important;
	font-size: 11px !important;
}
/* Import shared validation error styles */
@import '../shared/validation-error-styles.scss';
</style>
